import { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Breadcrumbs,
  Avatar,
  Divider,
  Skeleton,
  useMediaQuery,
  useTheme,
  Button,
  Link as MuiLink,
  Grid
} from "@mui/material";
import { Link, useParams, useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
// tracking removed
import backdrop from "../img/backdrop.png";
import screamifyLogo from "../img/screamifyweblogo.png";
import Header from "../components/Header";
import Footer from "../components/Footer";

// Social sharing components
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
  EmailIcon
} from 'react-share';

// DynamoDB API endpoint
const DYNAMODB_API_ENDPOINT = 'https://ke5sqc1hgc.execute-api.us-east-1.amazonaws.com/get-blog';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Header text styles that match the example
const headerStyle = {
  fontFamily: 'serif',
  fontWeight: 800,
  color: '#ffffff',
  textTransform: 'uppercase',
};

// Subtitle style for categories (REVIEWS, MOVIES, etc.)
const categoryStyle = {
  fontFamily: 'sans-serif',
  fontWeight: 700,
  color: '#666666',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  letterSpacing: '1px',
};

function BlogPostPage() {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams(); // This gets the blog post ID from the URL
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Truncate text to a specific length
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  // Fetch post from DynamoDB using the SK
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        
        // Decode the URL parameter since it's URL encoded (contains # character)
        const decodedId = decodeURIComponent(id);
        
        // Use blogSK parameter to get specific blog post (lambda expects blogSK, not SK)
        const url = `${DYNAMODB_API_ENDPOINT}?blogSK=${encodeURIComponent(decodedId)}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Post not found');
          }
          throw new Error(`API returned ${response.status}`);
        }
        
        const data = await response.json();
        
        // Convert DynamoDB format to match existing component expectations
        const convertedPost = {
          id: data.SK,
          fields: {
            Title: data.title,
            Content: data.content,
            Category: data.category,
            Author: data.author,
            Date: data.date,
            BlogId: data.SK,
            Pic1Url: data.pic1Url,
            Pic2Url: data.pic2Url,
            Pic3Url: data.pic3Url,
            HorrorTreeUrl: data.horrorTreeUrl
          }
        };
        
        setPost(convertedPost);
        
        // tracking removed
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err.message || 'Failed to load blog post. Please try again later.');
        
        // tracking removed
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchPost();
    } else {
      setError('No post ID provided in URL. Please select a post from the blog page.');
      setLoading(false);
    }
  }, [id]);
  
  // Track user engagement with the blog post
  useEffect(() => {
    if (!post || !post.fields) return;
    
    // Setup scroll tracking to detect reading progress (tracking removed)
    let readingTimer;
    let scrollDepth = 0;
    let timeSpent = 0;
    
    const trackScroll = () => {
      // Calculate how far the user has scrolled as a percentage
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const newScrollDepth = Math.round((scrollTop / docHeight) * 100);
      
      // Only track if scrolled to new depth milestone (25%, 50%, 75%, 100%)
      if (
        (newScrollDepth >= 25 && scrollDepth < 25) || 
        (newScrollDepth >= 50 && scrollDepth < 50) || 
        (newScrollDepth >= 75 && scrollDepth < 75) || 
        (newScrollDepth >= 90 && scrollDepth < 90)
      ) {
        scrollDepth = newScrollDepth;
      }
    };
    
    // Setup timer to track time spent on article
    const startReadingTimer = () => {
      readingTimer = setInterval(() => {
        timeSpent += 15;
        
        // tracking removed
        
      }, 15000); // Update every 15 seconds
    };
    
    // Add event listeners
    window.addEventListener('scroll', trackScroll);
    startReadingTimer();
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', trackScroll);
      clearInterval(readingTimer);
    };
  }, [post, id]);
  
  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };
  
  // Format date to match example (e.g., "32 minutes ago")
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };
  
  // Update the copyToClipboard function to include title and hashtags
  const copyToClipboard = (text) => {
    const formattedText = `${post.fields ? post.fields.Title : 'Horror Blog Article - Screamify'} ${text} #horrormovies #screamify via @screamify`;
    navigator.clipboard.writeText(formattedText).then(() => {
      // Show a toast or some feedback that URL was copied
      alert('Link copied to clipboard!');
    });
  };
  
  return (
    <Box 
      sx={{ 
        width: '100%', 
        minHeight: '100vh',
        backgroundImage: `url(${backdrop})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          zIndex: 0,
        }
      }}
    >
      <HelmetProvider>
        <Helmet>
          <title>{post && post.fields && post.fields.Title ? `${post.fields.Title} | Horror Blog - Screamify` : 'Horror Blog Article - Screamify'}</title>
          <meta name="description" content={post && post.fields ? (post.fields.Summary || truncateText(post.fields.Content || '', 160) || 'In-depth horror film analysis, reviews, and insights for horror fans and creators.') : 'In-depth horror film analysis, reviews, and insights for horror fans and creators.'} />
          <meta name="keywords" content={`horror, ${post && post.fields && post.fields.Category ? post.fields.Category.toLowerCase() : 'horror films'}, indie horror, ${post && post.fields && post.fields.Tags || 'horror movies, horror analysis'}, horror streaming, supernatural horror, psychological horror, slasher films, horror community, indie filmmakers`} />
          
          {/* Open Graph for Facebook, LinkedIn, etc. */}
          <meta property="og:type" content="article" />
          <meta property="og:url" content={`https://www.screamify.com/blog/${id}`} />
          <meta property="og:title" content={post && post.fields && post.fields.Title || 'Horror Blog Article - Screamify'} />
          <meta property="og:description" content={post && post.fields ? (post.fields.Summary || truncateText(post.fields.Content || '', 160) || 'In-depth horror film analysis, reviews, and insights for horror fans and creators.') : 'In-depth horror film analysis, reviews, and insights for horror fans and creators.'} />
          <meta property="og:image" content={post && post.fields && post.fields.Pic1Url || 'https://www.screamify.com/social-share.png'} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={post && post.fields && post.fields.Title ? `${post.fields.Title} - Featured Image` : 'Screamify Blog Post'} />
          <meta property="og:site_name" content="Screamify" />
          <meta property="article:published_time" content={post && post.fields && post.fields.Date || new Date().toISOString()} />
          <meta property="article:modified_time" content={post && post.fields && post.fields.UpdatedDate || post && post.fields && post.fields.Date || new Date().toISOString()} />
          <meta property="article:author" content={post && post.fields && post.fields.Author || 'Screamify Team'} />
          <meta property="article:section" content={post && post.fields && post.fields.Category || 'Horror'} />
          <meta property="article:tag" content={post && post.fields && post.fields.Tags || 'horror, indie horror, horror films'} />
          <meta property="fb:app_id" content="1030442901769744" />
          <meta property="og:locale" content="en_US" />
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@screamify" />
          <meta name="twitter:creator" content="@screamify" />
          <meta name="twitter:url" content={`https://www.screamify.com/blog/${id}`} />
          <meta name="twitter:title" content={post && post.fields && post.fields.Title || 'Horror Blog Article - Screamify'} />
          <meta name="twitter:description" content={post && post.fields ? (post.fields.Summary || truncateText(post.fields.Content || '', 160) || 'In-depth horror film analysis, reviews, and insights for horror fans and creators.') : 'In-depth horror film analysis, reviews, and insights for horror fans and creators.'} />
          <meta name="twitter:image" content={post && post.fields && post.fields.Pic1Url || 'https://www.screamify.com/social-share.png'} />
          <meta name="twitter:image:alt" content={post && post.fields && post.fields.Title ? `${post.fields.Title} - Featured Image` : 'Screamify Blog Post'} />
          
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="author" content={post && post.fields && post.fields.Author || 'Screamify Team'} />
          <link rel="canonical" href={`https://www.screamify.com/blog/${id}`} />
          
          {post && post.fields ? (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                "headline": post.fields.Title || 'Horror Blog Article',
                "name": post.fields.Title || 'Horror Blog Article',
                "image": [
                  post.fields.Pic1Url || 'https://www.screamify.com/social-share.png'
                ],
                "datePublished": post.fields.Date || new Date().toISOString(),
                "dateModified": post.fields.UpdatedDate || post.fields.Date || new Date().toISOString(),
                "author": {
                  "@type": "Person",
                  "name": post.fields.Author || 'Screamify Team',
                  "url": post.fields.HorrorTreeUrl || "https://www.screamify.com/authors"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Screamify",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://www.screamify.com/Favicon-01.png"
                  }
                },
                "description": post.fields.Summary || truncateText(post.fields.Content || '', 160) || 'In-depth horror film analysis, reviews, and insights for horror fans and creators.',
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://www.screamify.com/blog/${id}`
                },
                "keywords": `horror, ${post.fields.Category ? post.fields.Category.toLowerCase() : 'horror films'}, indie horror, ${post.fields.Tags || 'horror movies, horror analysis'}, horror streaming`,
                "genre": "Horror",
                "articleSection": post.fields.Category || "Horror",
                "wordCount": post.fields.Content ? post.fields.Content.split(/\s+/).length : 0,
                "isAccessibleForFree": "True",
                "speakable": {
                  "@type": "SpeakableSpecification",
                  "cssSelector": [".article-title", ".article-content"]
                },
                "about": {
                  "@type": "Thing",
                  "name": post.fields.Category || "Horror",
                  "description": "Horror film and entertainment"
                }
              })}
            </script>
          ) : null}
          
          {/* BreadcrumbList Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.screamify.com/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://www.screamify.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": post && post.fields ? post.fields.Title : "Blog Post",
                  "item": `https://www.screamify.com/blog/${id}`
                }
              ]
            })}
          </script>
        </Helmet>
      </HelmetProvider>
      
      <Header />
      
      <Box sx={{ height: '64px' }} /> {/* Spacer for header */}
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, flex: 1, py: 4 }}>
        {loading ? (
          // Loading state
          <Box sx={{ my: 4, animation: `${fadeIn} 1s ease-out` }}>
            <Skeleton 
              variant="text" 
              width="80%" 
              height={60} 
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 1 }} 
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Skeleton 
                variant="circular" 
                width={40} 
                height={40} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mr: 2 }} 
              />
              <Skeleton 
                variant="text" 
                width={120} 
                height={24} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
              />
            </Box>
            <Box sx={{ mb: 4 }}>
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={400} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '0', mb: 4 }} 
              />
            </Box>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Skeleton 
                key={item}
                variant="text" 
                width="100%" 
                height={24} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} 
              />
            ))}
          </Box>
        ) : error ? (
          // Error state
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 10,
              px: 2,
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 0, 0, 0.3)',
              animation: `${fadeIn} 1s ease-out`
            }}
          >
            <Typography variant="h5" sx={{ color: '#ff5555', mb: 2 }}>
              {error}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
              {error === 'Post not found' ? 
                "We couldn't find the blog post you're looking for. The post may have been removed or the URL is incorrect." : 
                error === 'No post ID provided in URL. Please select a post from the blog page.' ?
                "Please navigate to the blog page and select a post to read." :
                "We're having trouble loading this post. Please try again later."}
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to="/blog"
              sx={{
                backgroundColor: '#ff0000',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#cc0000'
                }
              }}
            >
              Return to Blog
            </Button>
          </Box>
        ) : !post ? (
          // No post found
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 10,
              px: 2,
              animation: `${fadeIn} 1s ease-out`
            }}
          >
            <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
              Post not found
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
              We couldn't find the blog post you're looking for.
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to="/blog"
              sx={{
                backgroundColor: '#ff0000',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#cc0000'
                }
              }}
            >
              Return to Blog
            </Button>
          </Box>
        ) : (
          // Post content
          <Box sx={{ animation: `${fadeIn} 1s ease-out` }}>
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                component={Link}
                to="/blog"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textTransform: 'none',
                  fontWeight: 500,
                  mb: 2,
                  '&:hover': {
                    color: '#ff0000',
                    backgroundColor: 'transparent'
                  }
                }}
              >
                Back to Blog
              </Button>
              
              <Breadcrumbs 
                aria-label="breadcrumb"
                separator="›"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)',
                  '& .MuiBreadcrumbs-separator': {
                    mx: 1,
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                <MuiLink 
                  component={Link} 
                  to="/"
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)',
                    textDecoration: 'none',
                    '&:hover': { color: '#ff0000', textDecoration: 'underline' }
                  }}
                >
                  Home
                </MuiLink>
                <MuiLink 
                  component={Link} 
                  to="/blog"
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)',
                    textDecoration: 'none',
                    '&:hover': { color: '#ff0000', textDecoration: 'underline' }
                  }}
                >
                  Blog
                </MuiLink>
                <Typography color="rgba(255, 255, 255, 0.8)">
                  {post && post.fields ? post.fields.Title : 'Blog Post'}
                </Typography>
              </Breadcrumbs>
            </Box>
            
            <Box sx={{ mb: 5 }}>
              <Typography
                sx={{
                  ...categoryStyle,
                  display: 'inline-block',
                  mr: 1.5,
                  mb: 2
                }}
              >
                {post && post.fields ? (post.fields.Category || 'UNCATEGORIZED') : 'UNCATEGORIZED'}
              </Typography>
              <Typography
                sx={{
                  display: 'inline-block',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.9rem',
                  fontWeight: 400,
                }}
              >
                / {post && post.fields ? formatTimeAgo(post.fields.Date) : ''}
              </Typography>
              
              <Typography
                variant="h1"
                sx={{
                  ...headerStyle,
                  color: 'white',
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                  lineHeight: 1.2,
                  mb: 3,
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                {post && post.fields ? post.fields.Title : 'Blog Post'}
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 4,
                  flexWrap: 'wrap',
                  gap: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                    By {post && post.fields && post.fields.HorrorTreeUrl ? (
                      <MuiLink 
                        href={post.fields.HorrorTreeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: '#ff0000',
                          textDecoration: 'none',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: '#ff5555'
                          }
                        }}
                      >
                        {post.fields.Author}
                      </MuiLink>
                    ) : (
                      post && post.fields ? post.fields.Author : 'Unknown Author'
                    )}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarTodayIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1 }} />
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {post && post.fields ? formatDate(post.fields.Date) : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Main Article Image (Pic1Url) */}
            <Box
              sx={{
                mb: 5,
                width: '100%',
                position: 'relative',
                paddingTop: '56.25%', // 16:9 aspect ratio
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src={post && post.fields ? post.fields.Pic1Url : ''}
                alt={post && post.fields ? post.fields.Title : 'Blog post image'}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </Box>
            
            {/* Article Content */}
            <Box 
              sx={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '0',
                p: { xs: 2, md: 4 },
                mb: 6
              }}
            >
              <Box sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.8, fontSize: '1.1rem' }}>
                {post && post.fields && post.fields.Content && (
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      h1: ({ node, ...props }) => <Typography variant="h3" sx={{ color: '#ff0000', my: 3, fontWeight: 600 }} {...props} />,
                      h2: ({ node, ...props }) => <Typography variant="h4" sx={{ color: '#ff0000', my: 3, fontWeight: 600 }} {...props} />,
                      h3: ({ node, ...props }) => <Typography variant="h5" sx={{ color: '#ff0000', my: 2, fontWeight: 600 }} {...props} />,
                      p: ({ node, ...props }) => <Typography paragraph sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3, lineHeight: 1.8 }} {...props} />,
                      a: ({ node, ...props }) => <MuiLink sx={{ color: '#ff0000', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }} {...props} />,
                      ul: ({ node, ...props }) => <Box component="ul" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3, pl: 2 }} {...props} />,
                      ol: ({ node, ...props }) => <Box component="ol" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3, pl: 2 }} {...props} />,
                      li: ({ node, ...props }) => <Box component="li" sx={{ mb: 1 }} {...props} />,
                      blockquote: ({ node, ...props }) => (
                        <Box 
                          component="blockquote" 
                          sx={{ 
                            pl: 3, 
                            borderLeft: '4px solid #ff0000',
                            ml: 0,
                            my: 4,
                            py: 1,
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontStyle: 'italic',
                            backgroundColor: 'rgba(255, 0, 0, 0.05)',
                            borderRadius: '0 8px 8px 0',
                          }} 
                          {...props} 
                        />
                      ),
                      code: ({ node, ...props }) => (
                        <Box 
                          component="code" 
                          sx={{ 
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            p: 0.5,
                            borderRadius: '4px',
                            color: '#ff9999',
                            fontFamily: 'monospace'
                          }} 
                          {...props} 
                        />
                      ),
                      strong: ({ node, ...props }) => <Box component="strong" sx={{ color: '#ff5555' }} {...props} />,
                      iframe: ({ node, ...props }) => (
                        <Box 
                          sx={{ 
                            my: 4, 
                            width: '100%',
                            position: 'relative',
                            paddingTop: '56.25%', // 16:9 aspect ratio for videos
                            overflow: 'hidden',
                            borderRadius: '4px',
                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)'
                          }}
                        >
                          <Box
                            component="iframe"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              border: 'none',
                            }}
                            {...props}
                            frameBorder="0"
                            allowFullScreen
                          />
                        </Box>
                      ),
                    }}
                  >
                    {post.fields.Content}
                  </ReactMarkdown>
                )}
              </Box>
            </Box>
            
            {/* Additional Images (Pic2Url and Pic3Url) */}
            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    width: '100%',
                    position: 'relative',
                    paddingTop: '56.25%', // 16:9 aspect ratio
                    overflow: 'hidden',
                    mb: { xs: 2, md: 0 },
                  }}
                >
                  <Box
                    component="img"
                    src={post && post.fields ? post.fields.Pic2Url : ''}
                    alt={post && post.fields ? post.fields.Title : 'Blog post image'}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    width: '100%',
                    position: 'relative',
                    paddingTop: '56.25%', // 16:9 aspect ratio
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={post && post.fields ? post.fields.Pic3Url : ''}
                    alt={post && post.fields ? post.fields.Title : 'Blog post image'}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
            
            {/* Author information */}
            <Box 
              sx={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                p: 3,
                mb: 6,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'flex-start' },
                gap: 3
              }}
            >
              {post && post.fields && post.fields.HorrorTreeUrl ? (
                <MuiLink
                  href={post.fields.HorrorTreeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: 'none',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Avatar 
                    alt={post.fields.Author} 
                    src={screamifyLogo} 
                    sx={{ 
                      width: { xs: 80, md: 100 }, 
                      height: { xs: 80, md: 100 },
                      border: '3px solid rgba(255, 0, 0, 0.5)',
                      bgcolor: 'black',
                      padding: '10px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 0 15px rgba(255, 0, 0, 0.7)',
                        border: '3px solid rgba(255, 0, 0, 0.8)',
                      },
                      '& .MuiAvatar-img': {
                        objectFit: 'contain'
                      }
                    }}
                  />
                </MuiLink>
              ) : (
                <Avatar 
                  alt={post && post.fields ? post.fields.Author : 'Author'} 
                  src={screamifyLogo} 
                  sx={{ 
                    width: { xs: 80, md: 100 }, 
                    height: { xs: 80, md: 100 },
                    border: '3px solid rgba(255, 0, 0, 0.5)',
                    bgcolor: 'black',
                    padding: '10px',
                    '& .MuiAvatar-img': {
                      objectFit: 'contain'
                    }
                  }}
                />
              )}
              <Box>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                  About {post && post.fields && post.fields.HorrorTreeUrl ? (
                    <MuiLink 
                      href={post.fields.HorrorTreeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: '#ff0000',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#ff5555'
                        }
                      }}
                    >
                      {post.fields.Author}
                    </MuiLink>
                  ) : (
                    post && post.fields ? post.fields.Author : 'the Author'
                  )}
                </Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: { xs: 'center', sm: 'left' } }}>
                  {post && post.fields ? post.fields.Author : 'The author'} is a passionate horror enthusiast and writer at Screamify. With a deep knowledge of indie horror cinema, they cover everything from film festivals to emerging trends in the genre.
                  {post && post.fields && post.fields.HorrorTreeUrl && (
                    <MuiLink 
                      href={post.fields.HorrorTreeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: '#ff0000',
                        display: 'inline-block',
                        ml: 1,
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        '&:hover': {
                          textDecoration: 'underline',
                        }
                      }}
                    >
                      View Horror Tree →
                    </MuiLink>
                  )}
                </Typography>
              </Box>
            </Box>
            
            {/* Social sharing buttons */}
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                p: 2,
                borderRadius: '4px'
              }}
            >
              <Typography 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.9rem',
                  mr: 1
                }}
              >
                <ShareIcon sx={{ mr: 1, fontSize: '1rem' }} />
                Share this article:
              </Typography>
              
              {post && (
                <>
                  <FacebookShareButton 
                    url={`https://www.screamify.com/blog/${id}`}
                    quote={post.fields ? post.fields.Title : 'Horror Blog Article - Screamify'}
                    hashtag="#horrormovies"
                  >
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  
                  <TwitterShareButton
                    url={`https://www.screamify.com/blog/${id}`}
                    title={post.fields ? post.fields.Title : 'Horror Blog Article - Screamify'}
                    via="screamify"
                    hashtags={['horrormovies', 'screamify']}
                  >
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  
                  <LinkedinShareButton
                    url={`https://www.screamify.com/blog/${id}`}
                    title={post.fields ? post.fields.Title : 'Horror Blog Article - Screamify'}
                    summary={`${post.fields ? post.fields.Title : ''} #horrormovies #screamify via @screamify`}
                    source="Screamify"
                  >
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                  
                  
                  
                  <WhatsappShareButton
                    url={`https://www.screamify.com/blog/${id}`}
                    title={`${post.fields ? post.fields.Title : 'Horror Blog Article - Screamify'} #horrormovies #screamify via @screamify`}
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  
                  <EmailShareButton
                    url={`https://www.screamify.com/blog/${id}`}
                    subject={post.fields ? post.fields.Title : 'Horror Blog Article - Screamify'}
                    body={`${post.fields ? post.fields.Title : 'Horror Blog Article - Screamify'} https://www.screamify.com/blog/${id} #horrormovies #screamify via @screamify\n\n`}
                  >
                    <EmailIcon size={32} round />
                  </EmailShareButton>
                  
                  <Button
                    onClick={() => copyToClipboard(`https://www.screamify.com/blog/${id}`)}
                    sx={{
                      minWidth: 'auto',
                      p: 0,
                      bgcolor: '#333',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: '#555'
                      },
                      color: 'white',
                      fontSize: '0.75rem'
                    }}
                  >
                    URL
                  </Button>
                </>
              )}
            </Box>
            
            {/* Navigation buttons */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mt: 6,
                mb: { xs: 8, sm: 6 }
              }}
            >
              <Button
                variant="outlined"
                component={Link}
                to="/blog"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 0, 0, 0.5)',
                  '&:hover': {
                    borderColor: '#ff0000',
                    backgroundColor: 'rgba(255, 0, 0, 0.1)'
                  },
                  textTransform: 'none',
                  px: 3,
                  py: 1
                }}
              >
                Back to All Articles
              </Button>
            </Box>
          </Box>
        )}
      </Container>
      
      <Footer />
    </Box>
  );
}

export default BlogPostPage; 